import React, { useState, useMemo, useCallback, memo } from 'react'
import {
    Card,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Typography,
    Box,
    useTheme,
} from '@material-ui/core'
import { Pagination } from '@material-ui/lab'
import { Selector } from '../../../../components'
import {
    Error as AlertCircleIcon,
    CheckCircle as CheckCircleIcon,
    Warning as AlertTriangleIcon,
} from '@material-ui/icons'
import { makeStyles } from '@material-ui/core/styles'
import { Property } from './DeadlineHome'

interface PaginationReturn {
    page: number
    setPage: (page: number) => void
    pageSize: number
    setPageSize: (size: number) => void
    numPages: number
    start: number
    end: number
}

interface PortfolioTableProps {
    properties: Property[]
    onPropertyClick: (property: Property) => void
    pagination: PaginationReturn
    totalCount: number
    sortKey: SortKey
    sortDirection: SortDirection
    onSort: (key: SortKey) => void
}

export type SortKey =
    | 'name'
    | 'progress'
    | 'completedTasks'
    | 'overdueTasks'
    | 'status'
export type SortDirection = 'asc' | 'desc'

const useStyles = makeStyles((theme) => ({
    card: {
        borderRadius: theme.shape.borderRadius * 2,
        boxShadow: theme.shadows[3],
        overflow: 'hidden',
        backgroundColor: theme.palette.background.paper,
    },
    header: {
        padding: theme.spacing(2, 4),
        borderBottom: `2px solid ${theme.palette.divider}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: theme.palette.grey[50],
        [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(2, 3),
        },
    },
    title: {
        fontSize: '1.25rem',
        fontWeight: 700,
        color: theme.palette.text.primary,
    },
    count: {
        fontSize: '0.875rem',
        color: theme.palette.text.secondary,
        fontWeight: 500,
        padding: theme.spacing(0.5, 1.5),
        backgroundColor: theme.palette.background.default,
        borderRadius: theme.shape.borderRadius,
    },
    tableContainer: {
        overflowX: 'auto',
        maxHeight: 'calc(100vh - 500px)',
        [theme.breakpoints.down('md')]: {
            maxHeight: 'calc(100vh - 600px)',
        },
    },
    table: {
        width: '100%',
    },
    tableHead: {
        backgroundColor: theme.palette.grey[100],
        '& .MuiTableCell-head': {
            borderBottom: `2px solid ${theme.palette.divider}`,
        },
    },
    tableCell: {
        padding: theme.spacing(2, 3),
        [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(1.5, 2),
        },
    },
    tableHeadCell: {
        fontSize: '0.75rem',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        color: theme.palette.text.secondary,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        '&:hover': {
            color: theme.palette.primary.main,
            backgroundColor: theme.palette.action.hover,
        },
        '& .MuiTableSortLabel-icon': {
            color: `${theme.palette.primary.main} !important`,
        },
    },
    tableRow: {
        cursor: 'pointer',
        transition: 'background-color 0.2s ease',
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
        '&:nth-of-type(even)': {
            backgroundColor: theme.palette.action.selected,
        },
        '&:nth-of-type(even):hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    propertyName: {
        fontSize: '0.9375rem',
        fontWeight: 600,
        color: theme.palette.text.primary,
        marginBottom: theme.spacing(0.5),
        '&:hover': {
            color: theme.palette.primary.main,
        },
    },
    propertyLocation: {
        fontSize: '0.8125rem',
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(0.25),
    },
    progressContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2.5),
        minWidth: 200,
        [theme.breakpoints.down('sm')]: {
            minWidth: 150,
            gap: theme.spacing(1.5),
        },
    },
    progressBarContainer: {
        flex: 1,
    },
    progressText: {
        fontSize: '0.8125rem',
        fontWeight: 600,
        color: theme.palette.text.secondary,
        minWidth: 65,
        textAlign: 'right',
    },
    completedText: {
        fontSize: '0.9375rem',
        fontWeight: 600,
        color: theme.palette.success.main,
    },
    overdueText: {
        fontSize: '0.9375rem',
        fontWeight: 600,
    },
    overdueTextDefault: {
        color: theme.palette.text.secondary,
    },
    overdueTextError: {
        color: theme.palette.error.main,
    },
    statusContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    statusIcon: {
        fontSize: '1.25rem',
    },
    paginationContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        padding: theme.spacing(2, 3),
        borderTop: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.grey[50],
        minHeight: 64,
        [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(1.5, 2),
            justifyContent: 'center',
        },
    },
    paginationControls: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
        [theme.breakpoints.down('sm')]: {
            flexWrap: 'wrap',
            justifyContent: 'center',
        },
    },
    paginationInfo: {
        fontSize: '0.875rem',
        color: theme.palette.text.secondary,
        fontWeight: 500,
    },
    rowsPerPageContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    rowsPerPageLabel: {
        fontSize: '0.875rem',
        color: theme.palette.text.secondary,
        whiteSpace: 'nowrap',
    },
}))

// Memoized table row component for better performance
interface PropertyRowProps {
    property: Property
    classes: any
    progressBarBgColor: string
    successColor: string
    primaryColor: string
    successLight: string
    primaryLight: string
    StatusIcon: React.ComponentType<{ status: Property['status'] }>
    onPropertyClick: (property: Property) => void
}

const PropertyRow = memo(
    ({
        property,
        classes,
        progressBarBgColor,
        successColor,
        primaryColor,
        successLight,
        primaryLight,
        StatusIcon,
        onPropertyClick,
    }: PropertyRowProps) => {
        const progress =
            property.totalTasks > 0
                ? Math.round(
                      (property.completedTasks / property.totalTasks) * 100,
                  )
                : 0
        const isComplete = progress === 100
        const hasOverdue = property.overdueTasks > 0

        const progressBarBgStyle: React.CSSProperties = {
            position: 'relative',
            width: '100%',
            height: 10,
            backgroundColor: progressBarBgColor,
            borderRadius: 5,
            overflow: 'hidden',
            boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.1)',
        }

        const progressBarStyle: React.CSSProperties = {
            position: 'absolute',
            top: 0,
            left: 0,
            height: '100%',
            width: `${progress}%`,
            backgroundColor: isComplete ? successColor : primaryColor,
            borderRadius: 5,
            boxShadow: isComplete
                ? `0 0 8px ${successLight}`
                : `0 0 8px ${primaryLight}`,
        }

        const handleClick = useCallback(() => {
            onPropertyClick(property)
        }, [property, onPropertyClick])

        return (
            <TableRow onClick={handleClick} className={classes.tableRow}>
                <TableCell className={classes.tableCell}>
                    <Box>
                        <Typography className={classes.propertyName}>
                            {property.name}
                        </Typography>
                        {property.location && (
                            <Typography className={classes.propertyLocation}>
                                {property.location}
                            </Typography>
                        )}
                    </Box>
                </TableCell>
                <TableCell className={classes.tableCell}>
                    <Box className={classes.progressContainer}>
                        <Box className={classes.progressBarContainer}>
                            <Box style={progressBarBgStyle}>
                                <Box style={progressBarStyle} />
                            </Box>
                        </Box>
                        <Typography className={classes.progressText}>
                            {property.completedTasks}/{property.totalTasks}
                        </Typography>
                    </Box>
                </TableCell>
                <TableCell className={classes.tableCell} align="center">
                    <Typography className={classes.completedText}>
                        {property.completedTasks}
                    </Typography>
                </TableCell>
                <TableCell className={classes.tableCell} align="center">
                    <Typography
                        className={`${classes.overdueText} ${
                            hasOverdue
                                ? classes.overdueTextError
                                : classes.overdueTextDefault
                        }`}
                    >
                        {property.overdueTasks}
                    </Typography>
                </TableCell>
                <TableCell className={classes.tableCell} align="center">
                    <Box className={classes.statusContainer}>
                        <StatusIcon status={property.status} />
                    </Box>
                </TableCell>
            </TableRow>
        )
    },
)

PropertyRow.displayName = 'PropertyRow'

export const PortfolioTable: React.FC<PortfolioTableProps> = ({
    properties,
    onPropertyClick,
    pagination,
    totalCount,
    sortKey,
    sortDirection,
    onSort,
}) => {
    const classes = useStyles()
    const theme = useTheme()

    const StatusIcon = memo(
        ({ status }: { status: Property['status'] }): JSX.Element | null => {
            switch (status) {
                case 'on-track':
                    return (
                        <CheckCircleIcon
                            className={classes.statusIcon}
                            style={{ color: theme.palette.success.main }}
                        />
                    )
                case 'at-risk':
                    return (
                        <AlertTriangleIcon
                            className={classes.statusIcon}
                            style={{ color: theme.palette.warning.main }}
                        />
                    )
                case 'behind':
                    return (
                        <AlertCircleIcon
                            className={classes.statusIcon}
                            style={{ color: theme.palette.error.main }}
                        />
                    )
                default:
                    return null
            }
        },
    )
    StatusIcon.displayName = 'StatusIcon'

    const createSortHandler = useCallback(
        (property: SortKey) => () => {
            onSort(property)
        },
        [onSort],
    )

    const handlePropertyClick = useCallback(
        (property: Property) => {
            onPropertyClick(property)
        },
        [onPropertyClick],
    )

    return (
        <Card className={classes.card}>
            <Box className={classes.header}>
                <Typography className={classes.title}>
                    Portfolio Standing
                </Typography>
                <Typography className={classes.count}>
                    {totalCount} properties
                </Typography>
            </Box>

            <TableContainer className={classes.tableContainer}>
                <Table className={classes.table} stickyHeader>
                    <TableHead className={classes.tableHead}>
                        <TableRow>
                            <TableCell
                                className={`${classes.tableCell} ${classes.tableHeadCell}`}
                            >
                                <TableSortLabel
                                    active={sortKey === 'name'}
                                    direction={
                                        sortKey === 'name'
                                            ? sortDirection
                                            : 'asc'
                                    }
                                    onClick={createSortHandler('name')}
                                >
                                    Name
                                </TableSortLabel>
                            </TableCell>
                            <TableCell
                                className={`${classes.tableCell} ${classes.tableHeadCell}`}
                            >
                                <TableSortLabel
                                    active={sortKey === 'progress'}
                                    direction={
                                        sortKey === 'progress'
                                            ? sortDirection
                                            : 'asc'
                                    }
                                    onClick={createSortHandler('progress')}
                                >
                                    Progress
                                </TableSortLabel>
                            </TableCell>
                            <TableCell
                                className={`${classes.tableCell} ${classes.tableHeadCell}`}
                                align="center"
                            >
                                <TableSortLabel
                                    active={sortKey === 'completedTasks'}
                                    direction={
                                        sortKey === 'completedTasks'
                                            ? sortDirection
                                            : 'asc'
                                    }
                                    onClick={createSortHandler(
                                        'completedTasks',
                                    )}
                                >
                                    Done
                                </TableSortLabel>
                            </TableCell>
                            <TableCell
                                className={`${classes.tableCell} ${classes.tableHeadCell}`}
                                align="center"
                            >
                                <TableSortLabel
                                    active={sortKey === 'overdueTasks'}
                                    direction={
                                        sortKey === 'overdueTasks'
                                            ? sortDirection
                                            : 'asc'
                                    }
                                    onClick={createSortHandler('overdueTasks')}
                                >
                                    Overdue
                                </TableSortLabel>
                            </TableCell>
                            <TableCell
                                className={`${classes.tableCell} ${classes.tableHeadCell}`}
                                align="center"
                            >
                                <TableSortLabel
                                    active={sortKey === 'status'}
                                    direction={
                                        sortKey === 'status'
                                            ? sortDirection
                                            : 'asc'
                                    }
                                    onClick={createSortHandler('status')}
                                >
                                    Status
                                </TableSortLabel>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {properties.map((property) => (
                            <PropertyRow
                                key={property.id}
                                property={property}
                                classes={classes}
                                progressBarBgColor={theme.palette.grey[200]}
                                successColor={theme.palette.success.main}
                                primaryColor={theme.palette.primary.main}
                                successLight={theme.palette.success.light}
                                primaryLight={theme.palette.primary.light}
                                StatusIcon={StatusIcon}
                                onPropertyClick={handlePropertyClick}
                            />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Pagination Controls */}
            <Box className={classes.paginationContainer}>
                <Box className={classes.paginationControls}>
                    <Box className={classes.rowsPerPageContainer}>
                        <Typography className={classes.rowsPerPageLabel}>
                            Rows per page:
                        </Typography>
                        <Selector
                            size="small"
                            data={[
                                { id: 10, value: 10 },
                                { id: 25, value: 25 },
                                { id: 50, value: 50 },
                                { id: 100, value: 100 },
                            ]}
                            onChange={(v) => {
                                pagination.setPageSize(Number(v.target.value))
                                pagination.setPage(1) // Reset to first page when page size changes
                            }}
                            currentValue={pagination.pageSize}
                            getDisplayString={(v) => `${v.value}`}
                        />
                    </Box>

                    <Pagination
                        page={pagination.page}
                        onChange={(_, newPage) => {
                            pagination.setPage(newPage)
                        }}
                        count={pagination.numPages}
                        color="primary"
                        size="small"
                    />

                    <Typography className={classes.paginationInfo}>
                        {pagination.start} - {pagination.end} of {totalCount}
                    </Typography>
                </Box>
            </Box>
        </Card>
    )
}
